<script setup lang="ts">
import { Deferred, Head, useForm, usePage } from '@inertiajs/vue3'
import { ChevronRight, Plus, Trash2 } from 'lucide-vue-next'
import { computed, reactive, ref, watch } from 'vue'

import AddressForm from '@/components/AddressForm.vue'
import AppAlert from '@/components/AppAlert.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import DobInput from '@/components/DobInput.vue'
import FormField from '@/components/FormField.vue'
import InputError from '@/components/InputError.vue'
import PartnerCombobox from '@/components/PartnerCombobox.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import ApplicationSidebar from '@/components/applications/ApplicationSidebar.vue'
import BankStatementTab from '@/components/applications/BankStatementTab.vue'
import DocumentsTab from '@/components/applications/DocumentsTab.vue'
import QuotationTab from '@/components/applications/QuotationTab.vue'
import SubRecordSection from '@/components/applications/SubRecordSection.vue'
import ChatLauncher from '@/components/chat/ChatLauncher.vue'
import { type Comment } from '@/components/comments/CommentItem.vue'
import CommentThread from '@/components/comments/CommentThread.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import AppLayout from '@/layouts/AppLayout.vue'
import { AU_STATES } from '@/lib/constants'
import { type ExpenseColumn } from '@/lib/constants'
import { formatLabel } from '@/lib/utils'
import {
  customerPath,
  editLoanApplicationPath,
  loanApplicationCommentsPath,
  loanApplicationsPath,
  updateAddressesPath,
  updateApplicationPath,
  updateAssetsPath,
  updateBankAccountsPath,
  updateEmploymentsPath,
  updateExpensesPath,
  updateLiabilitiesPath,
} from '@/routes'
import type { BreadcrumbItem } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────

interface CustomerData {
  id: number
  first_name: string
  last_name: string
  middle_name: string | null
  nickname: string | null
  gender: string
  date_of_birth: string
  email: string
  phone: string
}

interface Address {
  id?: number
  street_no: string
  street_name: string
  street_type: string
  unit_no: string
  city: string
  state: string
  postcode: string
  years_at_address: string
  months_at_address: string
  address_type: string
}

interface Employment {
  id?: number
  employment_status: string
  industry: string
  employment_title: string
  employer_name: string
  employer_phone: string
  employers_address: string
  years_at_employment: string
  months_at_employment: string
  employment_type: string
}

interface Asset {
  id?: number
  name: string
  value: string
  description: string
}

interface Liability {
  id?: number
  asset_id: string
  liability_type: string
  lender: string
  amount: string
  repayment: string
  description: string
  will_consolidate: boolean
}

interface BankAccount {
  id?: number
  name: string
  account: string
  bsb: string
  balance: string
  account_type: string
}

type Expense = Record<ExpenseColumn, string>

interface Application {
  id: string
  status: string
  status_at: string | null
  rejection_reason: string | null
  rejected_on: string | null
  loan_type: string
  purpose: string
  amount: number
  loan_term: number | null
  marital_status: string
  dependents: number
  monthly_income: number
  annual_income: number
  other_income: number | null
  spouse_income: number | null
  centrelink_income: boolean | null
  next_pay_date: string | null
  living_status: string
  submission_channel: string
  completed_at: string | null
  completed_in_full: boolean
  call_count: number
  driving_license: string | null
  driving_license_expiry: string | null
  driving_license_state: string | null
  card_number: string | null
  passport: string | null
  passport_expiry: string | null
  repayment_risk: boolean
  repayment_risk_details: string | null
  cr_score: number | null
  one_score: number | null
  id_matrix_status: string | null
  psa_generated_at: string | null
  customer_name: string
  partner_id: number
  partner_name: string
  customer: CustomerData
  addresses: Address[]
  employments: Employment[]
  expense: Expense | null
  bank_accounts: BankAccount[]
  assets: Asset[]
  liabilities: Liability[]
}

interface AppDocument {
  id: number
  name: string
  status: string
  rejection_reason: string | null
  reviewed_at: string | null
  created_at: string
  file_url: string | null
  file_name: string | null
  bank_statement: boolean
}

const props = defineProps<{
  application: Application
  upload_url: string
  document_info_requested_at: string | null
  bank_statement_reminder_sent_at: string | null
  rejection_reasons: Record<string, string>
  optional_doc_names: string[]
  bank_statement_link_options: { type: string; label: string; months: number }[]
  comments?: { data: Comment[]; has_more: boolean; load_more_url: string } | null
  documents?: AppDocument[] | null
  bank_statement_analyses?: Record<string, unknown>[] | null
  quotes?: Record<string, unknown>[] | null
  lenders?: Record<string, unknown>[] | null
  loan_terms: number[]
  loan_types: string[]
  purposes: Record<string, string[]>
  marital_statuses: string[]
  living_statuses: string[]
  genders: string[]
  employment_statuses: string[]
  employment_titles: string[]
  industries: string[]
  expense_columns: ExpenseColumn[]
  liability_types: string[]
  bank_account_types: string[]
  bank_names: string[]
}>()

// ── Breadcrumbs ────────────────────────────────────────────────────────────

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Loan Applications', href: loanApplicationsPath() },
  {
    title: props.application.customer_name,
    href: editLoanApplicationPath(props.application.id),
  },
]

// ── Tab state (driven by ?tab= query param) ────────────────────────────────

const TABS = ['overview', 'address', 'financials', 'documents', 'bank_statement', 'quotation'] as const
type Tab = (typeof TABS)[number]

function tabFromUrl(): Tab {
  const url = new URL(usePage().url, window.location.origin)
  const tab = url.searchParams.get('tab') as Tab | null
  return tab && TABS.includes(tab) ? tab : 'overview'
}

const activeTab = ref<Tab>(tabFromUrl())

// Keep activeTab in sync when Inertia navigates (e.g. after a section save redirect)
watch(
  () => usePage().url,
  () => {
    activeTab.value = tabFromUrl()
  },
)

function switchTab(tab: string | number) {
  activeTab.value = tab as Tab
  history.replaceState(null, '', `?tab=${tab}`)
}

// ── Helpers ────────────────────────────────────────────────────────────────

const toStr = (v: unknown) => (v == null ? '' : String(v))

const READONLY_STATUSES = ['rejected', 'settled'] as const
const isReadonly = computed(() =>
  READONLY_STATUSES.includes(props.application.status as (typeof READONLY_STATUSES)[number]),
)

const showQuotationTab = computed(
  () =>
    ['approved', 'settled', 'rejected'].includes(props.application.status) ||
    (props.application.status === 'accepted' && !!props.application.psa_generated_at),
)

// ── Form 1: Customer ───────────────────────────────────────────────────────

const customerForm = useForm({
  first_name: props.application.customer.first_name,
  last_name: props.application.customer.last_name,
  middle_name: props.application.customer.middle_name ?? '',
  nickname: props.application.customer.nickname ?? '',
  gender: props.application.customer.gender,
  date_of_birth: toStr(props.application.customer.date_of_birth),
  email: props.application.customer.email,
  phone: props.application.customer.phone,
})

function saveCustomer() {
  customerForm
    .transform((data) => ({ ...data, loan_application_id: props.application.id }))
    .patch(customerPath(props.application.customer.id), { preserveScroll: true })
}

// ── Form 2: Application + Identity ────────────────────────────────────────

const selectedPartnerName = ref(props.application.partner_name)

const appForm = useForm({
  partner_id: toStr(props.application.partner_id),
  loan_type: props.application.loan_type,
  purpose: props.application.purpose,
  amount: toStr(props.application.amount),
  loan_term: toStr(props.application.loan_term ?? ''),
  dependents: toStr(props.application.dependents),
  monthly_income: toStr(props.application.monthly_income),
  annual_income: toStr(props.application.annual_income),
  other_income: toStr(props.application.other_income ?? ''),
  spouse_income: toStr(props.application.spouse_income ?? ''),
  centrelink_income: props.application.centrelink_income ?? false,
  next_pay_date: props.application.next_pay_date ?? '',
  living_status: props.application.living_status,
  marital_status: props.application.marital_status,
  driving_license: props.application.driving_license ?? '',
  driving_license_expiry: toStr(props.application.driving_license_expiry ?? ''),
  driving_license_state: props.application.driving_license_state ?? '',
  card_number: props.application.card_number ?? '',
  passport: props.application.passport ?? '',
  passport_expiry: toStr(props.application.passport_expiry ?? ''),
})

const availablePurposes = computed(() => props.purposes[appForm.loan_type] ?? [])
const purposeError = ref('')

// Licence / passport must expire at least 28 days from today
const minExpiryDate = new Date(Date.now() + 28 * 86_400_000).toISOString().split('T')[0]

function saveApplication() {
  if (!appForm.purpose) {
    purposeError.value = 'Purpose is required'
    return
  }
  purposeError.value = ''
  appForm.patch(`${updateApplicationPath(props.application.id)}?tab=${activeTab.value}`, {
    preserveScroll: true,
  })
}

// ── Form 3: Addresses ─────────────────────────────────────────────────────

const defaultAddress = (type: string): Address => ({
  street_no: '',
  street_name: '',
  street_type: '',
  unit_no: '',
  city: '',
  state: '',
  postcode: '',
  years_at_address: '',
  months_at_address: '',
  address_type: type,
})

const existingCurrent = props.application.addresses.find((a) => a.address_type === 'current')
const existingPrevious = props.application.addresses.find((a) => a.address_type === 'previous')

const currentAddress = reactive<Address>(
  existingCurrent
    ? {
        ...existingCurrent,
        years_at_address: toStr(existingCurrent.years_at_address),
        months_at_address: toStr(existingCurrent.months_at_address),
      }
    : defaultAddress('current'),
)

const previousAddress = reactive<Address>(
  existingPrevious
    ? {
        ...existingPrevious,
        years_at_address: toStr(existingPrevious.years_at_address),
        months_at_address: toStr(existingPrevious.months_at_address),
      }
    : defaultAddress('previous'),
)

const needsPreviousAddress = computed(() => {
  const years = parseInt(currentAddress.years_at_address)
  return !isNaN(years) && years < 3
})

const addressForm = useForm({})
const addressError = ref('')

function validateAddressDuration(addr: Address, label: string): string | null {
  if (!addr.years_at_address) return `${label}: years at address is required`
  if (Number(addr.years_at_address) < 5 && !addr.months_at_address) return `${label}: months at address is required`
  return null
}

function saveAddresses() {
  addressError.value = ''

  const currentErr = validateAddressDuration(currentAddress, 'Current address')
  if (currentErr) {
    addressError.value = currentErr
    return
  }

  if (needsPreviousAddress.value) {
    const prevErr = validateAddressDuration(previousAddress, 'Previous address')
    if (prevErr) {
      addressError.value = prevErr
      return
    }
  }

  addressForm
    .transform(() => ({
      addresses: {
        current: currentAddress,
        previous: needsPreviousAddress.value ? previousAddress : undefined,
      },
    }))
    .patch(`${updateAddressesPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
    })
}

// ── Form 4: Employments ────────────────────────────────────────────────────

const employments = reactive<Employment[]>(
  props.application.employments.map((e) => ({
    ...e,
    years_at_employment: toStr(e.years_at_employment),
    months_at_employment: toStr(e.months_at_employment),
  })),
)

const currentEmploymentMonths = computed(() =>
  employments
    .filter((e) => e.employment_type === 'current')
    .reduce((sum, e) => sum + (parseInt(e.years_at_employment) || 0) * 12 + (parseInt(e.months_at_employment) || 0), 0),
)

const needsPreviousEmployment = computed(
  () =>
    employments.some((e) => e.employment_type === 'current' && e.years_at_employment !== '') &&
    currentEmploymentMonths.value < 24,
)

const hasCurrentEmployment = computed(() => employments.some((e) => e.employment_type === 'current'))
const hasPreviousEmployment = computed(() => employments.some((e) => e.employment_type === 'previous'))
const hasSecondaryEmployment = computed(() => employments.some((e) => e.employment_type === 'secondary'))

watch(
  needsPreviousEmployment,
  (needs) => {
    if (needs && !hasPreviousEmployment.value) {
      employments.push({
        employment_status: '',
        industry: '',
        employment_title: '',
        employer_name: '',
        employer_phone: '',
        employers_address: '',
        years_at_employment: '',
        months_at_employment: '',
        employment_type: 'previous',
      })
    } else if (!needs) {
      // Remove blank auto-added previous employment entries
      for (let i = employments.length - 1; i >= 0; i--) {
        const e = employments[i]
        if (
          e.employment_type === 'previous' &&
          !e.employment_status &&
          !e.employment_title &&
          !e.employer_name &&
          !e.years_at_employment
        )
          employments.splice(i, 1)
      }
    }
  },
  { immediate: true },
)

const employmentForm = useForm({})
const employmentError = ref('')

function addEmployment(type: 'current' | 'secondary') {
  employments.push({
    employment_status: '',
    industry: '',
    employment_title: '',
    employer_name: '',
    employer_phone: '',
    employers_address: '',
    years_at_employment: '',
    months_at_employment: '',
    employment_type: type,
  })
}

function saveEmployments() {
  employmentError.value = ''
  if (needsPreviousEmployment.value) {
    const prev = employments.find((e) => e.employment_type === 'previous')
    if (!prev?.employer_name) {
      employmentError.value = 'Previous employment must be filled — current employment is less than 2 years.'
      return
    }
  }
  employmentForm
    .transform(() => ({
      employments: {
        current: employments.find((e) => e.employment_type === 'current'),
        previous: employments.find((e) => e.employment_type === 'previous'),
        secondary: employments.find((e) => e.employment_type === 'secondary'),
      },
    }))
    .patch(`${updateEmploymentsPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
    })
}

// ── Form 5: Expenses ───────────────────────────────────────────────────────

const defaultExpense = () => Object.fromEntries(props.expense_columns.map((c) => [c, '0'])) as Expense

const expense = reactive<Expense>(
  props.application.expense
    ? (Object.fromEntries(props.expense_columns.map((c) => [c, toStr(props.application.expense![c])])) as Expense)
    : defaultExpense(),
)

const expenseTotal = computed(() =>
  props.expense_columns.reduce((sum, col) => sum + (parseFloat(expense[col]) || 0), 0),
)

const expenseForm = useForm({})

function saveExpenses() {
  expenseForm
    .transform(() => ({ expense }))
    .patch(`${updateExpensesPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
    })
}

// ── Form 6: Bank accounts ─────────────────────────────────────────────────

const bankAccounts = reactive<BankAccount[]>(
  props.application.bank_accounts.map((b) => ({ ...b, balance: toStr(b.balance) })),
)

const bankAccountForm = useForm({})

function addBankAccount() {
  bankAccounts.push({ name: '', account: '', bsb: '', balance: '', account_type: '' })
}

function saveBankAccounts() {
  bankAccountForm
    .transform(() => ({ bank_accounts: bankAccounts }))
    .patch(`${updateBankAccountsPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
    })
}

// ── Form 7: Assets ────────────────────────────────────────────────────────

const assets = reactive<Asset[]>(props.application.assets.map((a) => ({ ...a, value: toStr(a.value) })))

const assetForm = useForm({})

function addAsset() {
  assets.push({ name: '', value: '', description: '' })
}

function saveAssets() {
  assetForm
    .transform(() => ({ assets }))
    .patch(`${updateAssetsPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
      onSuccess: () => {
        // Sync IDs returned by the server back into the reactive array so
        // newly saved assets appear in the secured_loan asset selector.
        props.application.assets.forEach((a, i) => {
          if (assets[i] !== undefined) assets[i].id = a.id
        })
      },
    })
}

// ── Form 8: Liabilities ────────────────────────────────────────────────────

const liabilities = reactive<Liability[]>(
  props.application.liabilities.map((l) => ({
    ...l,
    amount: toStr(l.amount),
    repayment: toStr(l.repayment),
    asset_id: toStr(l.asset_id),
  })),
)

const isSecurable = (type: string) => ['investment_mortgage', 'occupied_mortgage', 'secured_loan'].includes(type)

const liabilityAmountLabel = (type: string) => (type === 'credit_card' ? 'Card Limit ($)' : 'Amount ($)')
const liabilityRepaymentLabel = (type: string) => (type === 'credit_card' ? 'Owing ($)' : 'Repayment ($)')
const liabilityDescriptionLabel = (type: string) => (type === 'bnpl' ? 'Limit ($)' : 'Description')

const liabilityForm = useForm({})

function addLiability() {
  liabilities.push({
    asset_id: '',
    liability_type: '',
    lender: '',
    amount: '',
    repayment: '',
    description: '',
    will_consolidate: false,
  })
}

function saveLiabilities() {
  liabilityForm
    .transform(() => ({ liabilities }))
    .patch(`${updateLiabilitiesPath(props.application.id)}?tab=${activeTab.value}`, {
      preserveScroll: true,
    })
}

function onLiabilityTypeChange(index: number, value: string) {
  liabilities[index].liability_type = value
  if (value !== 'secured_loan') liabilities[index].asset_id = ''
}

function securedLiabilitiesFor(asset: Asset) {
  if (!asset.id) return []
  return liabilities.filter((l) => l.asset_id === String(asset.id))
}
</script>

<template>
  <Head :title="`Processing — ${application.customer_name}`" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex flex-col-reverse gap-6 px-4 py-6 lg:flex-row">
      <!-- ── Main content ──────────────────────────────────────────────── -->
      <div class="min-w-0 flex-1">
        <AppAlert v-if="application.repayment_risk" variant="warning" title="Anticipated repayment risk:" class="mb-4">
          {{ application.repayment_risk_details }}
        </AppAlert>
        <AppAlert v-if="isReadonly" variant="warning" class="mb-4">
          This application is <strong class="font-semibold capitalize">{{ application.status }}</strong> and cannot be edited.
        </AppAlert>

        <Tabs v-model="activeTab" @update:model-value="switchTab">
          <TabsList class="mb-6 w-full justify-start">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="address">Addresses</TabsTrigger>
            <TabsTrigger value="financials">Financials</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="bank_statement">Bank Statement</TabsTrigger>
            <TabsTrigger v-if="showQuotationTab" value="quotation">Quotation</TabsTrigger>
          </TabsList>

          <!-- ── Tab 1: Overview ─────────────────────────────────────── -->
          <TabsContent value="overview" class="space-y-6">
            <!-- Personal Information -->
            <Card>
              <CardHeader>
                <CardTitle class="text-base">Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form class="space-y-4" @submit.prevent="saveCustomer">
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="First Name" required :errors="customerForm.errors.first_name">
                      <Input v-model="customerForm.first_name" required />
                    </FormField>
                    <FormField label="Middle Name">
                      <Input v-model="customerForm.middle_name" placeholder="Optional" />
                    </FormField>
                    <FormField label="Last Name" required :errors="customerForm.errors.last_name">
                      <Input v-model="customerForm.last_name" required />
                    </FormField>
                  </div>
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Date of Birth" required :errors="customerForm.errors.date_of_birth">
                      <DobInput v-model="customerForm.date_of_birth" />
                    </FormField>
                    <FormField label="Email" required :errors="customerForm.errors.email">
                      <Input v-model="customerForm.email" type="email" required />
                    </FormField>
                    <FormField label="Gender" required :errors="customerForm.errors.gender">
                      <Select v-model="customerForm.gender">
                        <SelectTrigger class="w-full"><SelectValue placeholder="Select gender" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="g in props.genders" :key="g" :value="g">
                            {{ formatLabel(g) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Phone" required :errors="customerForm.errors.phone">
                      <PhoneInput v-model="customerForm.phone" />
                    </FormField>
                    <FormField label="Preferred Name">
                      <Input v-model="customerForm.nickname" placeholder="Optional" />
                    </FormField>
                  </div>
                  <div class="flex justify-end">
                    <Button type="submit" :disabled="customerForm.processing || isReadonly">Save Customer</Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <!-- Application Information + Identification Documents -->
            <Card>
              <CardHeader>
                <CardTitle class="text-base">Application & Identification</CardTitle>
              </CardHeader>
              <CardContent>
                <form class="space-y-4" @submit.prevent="saveApplication">
                  <FormField label="Partner" required>
                    <PartnerCombobox
                      v-model="appForm.partner_id"
                      :partner-name="selectedPartnerName"
                      :disabled="isReadonly"
                      @update:partner-name="selectedPartnerName = $event"
                    />
                  </FormField>
                  <!-- Application details section -->
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Loan Type" required>
                      <Select v-model="appForm.loan_type">
                        <SelectTrigger class="w-full"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="t in props.loan_types" :key="t" :value="t">
                            {{ formatLabel(t) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Purpose" required :errors="purposeError ? [purposeError] : undefined">
                      <Select
                        v-model="appForm.purpose"
                        :disabled="!appForm.loan_type"
                        @update:model-value="purposeError = ''"
                      >
                        <SelectTrigger class="w-full"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="p in availablePurposes" :key="p" :value="p">
                            {{ formatLabel(p) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Amount ($)" required :errors="appForm.errors.amount">
                      <Input
                        v-model="appForm.amount"
                        type="number"
                        min="2000"
                        :max="appForm.loan_type === 'vehicle' ? 150000 : 72000"
                        required
                      />
                    </FormField>
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Loan Term">
                      <Select v-model="appForm.loan_term">
                        <SelectTrigger class="w-full">
                          <SelectValue placeholder="Select term" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="t in props.loan_terms" :key="t" :value="String(t)">
                            {{ t }} months
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Living Status" required>
                      <Select v-model="appForm.living_status">
                        <SelectTrigger class="w-full"><SelectValue placeholder="Select status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="s in props.living_statuses" :key="s" :value="s">
                            {{ formatLabel(s) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Dependents" required>
                      <Select v-model="appForm.dependents">
                        <SelectTrigger class="w-full">
                          <SelectValue placeholder="Select dependents" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="d in 10" :key="d - 1" :value="String(d - 1)">
                            {{ d - 1 }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Annual Income ($)" required>
                      <Input v-model="appForm.annual_income" type="number" min="0" required />
                    </FormField>
                    <FormField label="Monthly Income ($)" required>
                      <Input v-model="appForm.monthly_income" type="number" min="0" required />
                    </FormField>
                    <FormField label="Other Income ($)">
                      <Input v-model="appForm.other_income" type="number" min="0" placeholder="Optional" />
                    </FormField>
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Marital Status" required>
                      <Select v-model="appForm.marital_status">
                        <SelectTrigger class="w-full"><SelectValue placeholder="Select status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="s in props.marital_statuses" :key="s" :value="s">
                            {{ formatLabel(s) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Spouse Income ($)">
                      <Input v-model="appForm.spouse_income" type="number" min="0" placeholder="Optional" />
                    </FormField>
                    <FormField label="Next Pay Date">
                      <AppDatePicker v-model="appForm.next_pay_date" placeholder="Optional" />
                    </FormField>
                    <FormField label="Centrelink Income">
                      <div class="flex h-9 items-center">
                        <Checkbox
                          id="centrelink_income"
                          :model-value="appForm.centrelink_income"
                          @update:model-value="(v) => (appForm.centrelink_income = !!v)"
                        />
                      </div>
                    </FormField>
                  </div>

                  <Separator />

                  <!-- Identity documents section -->
                  <p class="text-muted-foreground text-sm font-medium">Identity Documents</p>

                  <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <FormField label="Driving Licence No.">
                      <Input v-model="appForm.driving_license" placeholder="Optional" />
                    </FormField>
                    <FormField label="Licence Card No.">
                      <Input v-model="appForm.card_number" placeholder="Optional" />
                    </FormField>
                    <FormField label="Licence State">
                      <Select v-model="appForm.driving_license_state">
                        <SelectTrigger class="w-full"><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="s in AU_STATES" :key="s" :value="s">{{ s }}</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Licence Expiry">
                      <AppDatePicker
                        v-model="appForm.driving_license_expiry"
                        :min="minExpiryDate"
                        placeholder="Select expiry"
                      />
                    </FormField>
                  </div>

                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Passport No.">
                      <Input v-model="appForm.passport" placeholder="Optional" />
                    </FormField>
                    <FormField label="Passport Expiry">
                      <AppDatePicker
                        v-model="appForm.passport_expiry"
                        :min="minExpiryDate"
                        placeholder="Select expiry"
                      />
                    </FormField>
                  </div>

                  <div class="flex justify-end">
                    <Button type="submit" :disabled="appForm.processing || isReadonly">Save Application</Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <!-- Employment -->
            <Card>
              <CardHeader>
                <div class="flex items-center justify-between">
                  <CardTitle class="text-base">Employment(s)</CardTitle>
                  <Button
                    v-if="!hasCurrentEmployment"
                    type="button"
                    variant="outline"
                    size="sm"
                    :disabled="isReadonly"
                    @click="addEmployment('current')"
                  >
                    <Plus class="mr-1 size-4" />
                    Add
                  </Button>
                  <Button
                    v-else-if="!hasSecondaryEmployment"
                    type="button"
                    variant="outline"
                    size="sm"
                    :disabled="isReadonly"
                    @click="addEmployment('secondary')"
                  >
                    <Plus class="mr-1 size-4" />
                    Add Secondary
                  </Button>
                </div>
              </CardHeader>
              <CardContent class="space-y-4">
                <form @submit.prevent="saveEmployments">
                  <AppAlert v-if="needsPreviousEmployment && !hasPreviousEmployment" variant="warning">
                    Previous employment is required — current employment is less than 2 years.
                  </AppAlert>
                  <div v-if="!employments.length" class="text-muted-foreground py-4 text-center text-sm">
                    No employment added
                  </div>
                  <div v-for="(emp, i) in employments" :key="i" class="border-border rounded-md border p-4">
                    <div class="mb-3 flex items-center justify-between">
                      <Badge
                        :class="{
                          'border-green-200 bg-green-100 text-green-800': emp.employment_type === 'current',
                          'border-amber-200 bg-amber-100 text-amber-800': emp.employment_type === 'previous',
                          'border-blue-200 bg-blue-100 text-blue-800': emp.employment_type === 'secondary',
                        }"
                      >
                        {{
                          emp.employment_type === 'current'
                            ? 'Current'
                            : emp.employment_type === 'previous'
                              ? 'Previous'
                              : 'Secondary'
                        }}
                      </Badge>
                      <span
                        v-if="emp.employment_type === 'previous' && needsPreviousEmployment"
                        class="text-muted-foreground text-sm font-normal"
                      >
                        &nbsp; — required, less than 2 years at current</span
                      >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        class="text-destructive ml-auto h-6 px-2"
                        @click="employments.splice(i, 1)"
                      >
                        <Trash2 class="size-3" />
                      </Button>
                    </div>
                    <div class="space-y-3">
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <FormField label="Employment Status" required>
                          <Select v-model="employments[i].employment_status">
                            <SelectTrigger class="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem v-for="s in props.employment_statuses" :key="s" :value="s">
                                {{ formatLabel(s) }}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                        <FormField label="Employment Title" required>
                          <Select v-model="employments[i].employment_title">
                            <SelectTrigger class="w-full"><SelectValue placeholder="Select title" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem v-for="t in props.employment_titles" :key="t" :value="t">{{ t }}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                        <FormField label="Employer Name" required>
                          <Input v-model="employments[i].employer_name" placeholder="Employer name" required />
                        </FormField>
                      </div>
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <FormField label="Years" required>
                          <Select v-model="employments[i].years_at_employment">
                            <SelectTrigger class="w-full"><SelectValue placeholder="Select years" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem v-for="y in 6" :key="y - 1" :value="String(y - 1)">
                                {{ y - 1 === 5 ? '5+ years' : `${y - 1} ${y - 1 === 1 ? 'year' : 'years'}` }}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                        <FormField label="Months">
                          <Select v-model="employments[i].months_at_employment">
                            <SelectTrigger class="w-full"><SelectValue placeholder="Select months" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem v-for="m in 12" :key="m - 1" :value="String(m - 1)">
                                {{ m - 1 === 1 ? '1 month' : `${m - 1} months` }}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                      </div>
                      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <FormField label="Industry">
                          <Select v-model="employments[i].industry">
                            <SelectTrigger class="w-full"><SelectValue placeholder="Select industry" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem v-for="t in props.industries" :key="t" :value="t">{{
                                t.toUpperCase()
                              }}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                        <FormField label="Employer Phone">
                          <Input v-model="employments[i].employer_phone" placeholder="Optional" />
                        </FormField>
                        <FormField label="Employer Address">
                          <Input v-model="employments[i].employers_address" placeholder="Optional" />
                        </FormField>
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 space-y-3">
                    <InputError :messages="employmentError ? [employmentError] : []" />
                    <div class="flex justify-end">
                      <Button type="submit" :disabled="employmentForm.processing || isReadonly">Save Employment</Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <!-- ── Tab 2: Addresses ────────────────────────── -->
          <TabsContent value="address" class="space-y-6">
            <!-- Address -->
            <Card>
              <CardHeader>
                <CardTitle class="text-base">Addresses</CardTitle>
              </CardHeader>
              <CardContent>
                <form class="space-y-6" @submit.prevent="saveAddresses">
                  <!-- Current address -->
                  <div class="space-y-3">
                    <p class="text-sm font-medium">Current Address</p>
                    <AddressForm
                      :model-value="currentAddress"
                      show-duration
                      @update:model-value="Object.assign(currentAddress, $event)"
                    />
                  </div>

                  <!-- Previous address (conditional) -->
                  <div v-if="needsPreviousAddress" class="space-y-3">
                    <Separator />
                    <p class="text-sm font-medium">
                      Previous Address
                      <span class="text-muted-foreground font-normal"> — required, less than 3 years at current</span>
                    </p>
                    <AddressForm
                      :model-value="previousAddress"
                      show-duration
                      @update:model-value="Object.assign(previousAddress, $event)"
                    />
                  </div>

                  <InputError :messages="addressError ? [addressError] : []" />

                  <div class="flex justify-end">
                    <Button type="submit" :disabled="addressForm.processing || isReadonly">Save Address</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <!-- ── Tab 3: Financials ───────────────────────────────────── -->
          <TabsContent value="financials" class="space-y-6">
            <!-- Monthly Expenses -->
            <Card>
              <CardHeader>
                <CardTitle class="text-base">Monthly Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div v-for="col in props.expense_columns" :key="col" class="grid gap-2">
                    <Label class="text-xs text-gray-400">{{ formatLabel(col) }}</Label>
                    <Input v-model="expense[col]" type="number" min="0" />
                  </div>
                </div>
                <div class="border-border mt-4 flex items-center justify-between border-t pt-4">
                  <div class="flex items-center gap-3">
                    <Label class="text-muted-foreground text-sm">Total monthly expenses</Label>
                    <div
                      class="bg-muted text-foreground rounded-md border px-3 py-1.5 text-sm font-medium tabular-nums"
                    >
                      ${{ expenseTotal.toFixed(2) }}
                    </div>
                  </div>
                  <Button :disabled="expenseForm.processing || isReadonly" @click="saveExpenses">Save Expenses</Button>
                </div>
              </CardContent>
            </Card>

            <!-- Bank Accounts -->
            <form @submit.prevent="saveBankAccounts">
              <SubRecordSection
                title="Bank Accounts"
                :items="bankAccounts"
                @add="addBankAccount"
                @remove="(i) => bankAccounts.splice(i, 1)"
              >
                <template #default="{ index }">
                  <div class="space-y-3">
                    <div class="grid grid-cols-5 gap-3">
                      <FormField label="Bank Name" required>
                        <Select v-model="bankAccounts[index].name">
                          <SelectTrigger class="w-full"><SelectValue placeholder="Bank" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem v-for="t in props.bank_names" :key="t" :value="t">
                              {{ formatLabel(t) }}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField label="Account Type" required>
                        <Select v-model="bankAccounts[index].account_type" required>
                          <SelectTrigger class="w-full"><SelectValue placeholder="Account type" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem v-for="t in props.bank_account_types" :key="t" :value="t">
                              {{ formatLabel(t) }}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormField>
                      <FormField label="Account Number">
                        <Input v-model="bankAccounts[index].account" placeholder="123456789" />
                      </FormField>
                      <FormField label="BSB">
                        <Input v-model="bankAccounts[index].bsb" placeholder="Optional" />
                      </FormField>
                      <FormField label="Balance ($)">
                        <Input v-model="bankAccounts[index].balance" type="number" min="0" />
                      </FormField>
                    </div>
                  </div>
                </template>
              </SubRecordSection>

              <div class="mt-4 flex justify-end">
                <Button type="submit" :disabled="bankAccountForm.processing || isReadonly">Save Bank Accounts</Button>
              </div>
            </form>

            <!-- Assets -->
            <form @submit.prevent="saveAssets">
              <SubRecordSection title="Assets" :items="assets" @add="addAsset" @remove="(i) => assets.splice(i, 1)">
                <template #default="{ index }">
                  <div class="space-y-3">
                    <div class="grid grid-cols-3 gap-3">
                      <FormField label="Asset Name" required>
                        <Input v-model="assets[index].name" required />
                      </FormField>
                      <FormField label="Value ($)" required>
                        <Input v-model="assets[index].value" type="number" min="0" required />
                      </FormField>
                      <FormField label="Description">
                        <Input v-model="assets[index].description" placeholder="Optional" />
                      </FormField>
                    </div>
                    <!-- Read-only: liabilities secured against this asset -->
                    <div v-if="securedLiabilitiesFor(assets[index]).length > 0">
                      <div v-for="(l, li) in securedLiabilitiesFor(assets[index])" :key="li" class="flex items-center">
                        <div class="border-border relative -top-3.75 -mr-2 ml-8 h-8 w-4 border-b-2 border-l-2" />
                        <ChevronRight class="text-border mr-0.5 size-5 shrink-0" />
                        <div
                          class="border-border bg-muted grid flex-1 grid-cols-2 gap-3 rounded-md border p-4 sm:grid-cols-4"
                        >
                          <FormField label="Type">
                            <Input :model-value="formatLabel(l.liability_type)" readonly />
                          </FormField>
                          <FormField label="Lender">
                            <Input :model-value="l.lender || '—'" readonly />
                          </FormField>
                          <FormField label="Amount ($)">
                            <Input :model-value="l.amount" readonly />
                          </FormField>
                          <FormField label="Repayment ($)">
                            <Input :model-value="l.repayment" readonly />
                          </FormField>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </SubRecordSection>

              <div class="mt-4 flex justify-end">
                <Button type="submit" :disabled="assetForm.processing || isReadonly">Save Assets</Button>
              </div>
            </form>

            <!-- Liabilities -->
            <form @submit.prevent="saveLiabilities">
              <SubRecordSection
                title="Liabilities"
                :items="liabilities"
                @add="addLiability"
                @remove="(i) => liabilities.splice(i, 1)"
              >
                <template #default="{ index }">
                  <div class="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                    <FormField label="Type" required>
                      <Select
                        :model-value="liabilities[index].liability_type"
                        @update:model-value="(v) => onLiabilityTypeChange(index, String(v ?? ''))"
                      >
                        <SelectTrigger class="w-full"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="t in props.liability_types" :key="t" :value="t">
                            {{ formatLabel(t) }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Lender">
                      <Input v-model="liabilities[index].lender" placeholder="Optional" />
                    </FormField>
                    <FormField :label="liabilityAmountLabel(liabilities[index].liability_type)" required>
                      <Input v-model="liabilities[index].amount" type="number" min="0" required />
                    </FormField>
                    <FormField :label="liabilityRepaymentLabel(liabilities[index].liability_type)" required>
                      <Input v-model="liabilities[index].repayment" type="number" min="0" required />
                    </FormField>
                    <FormField v-if="isSecurable(liabilities[index].liability_type)" label="Secured Asset">
                      <Select v-model="liabilities[index].asset_id">
                        <SelectTrigger class="w-full"><SelectValue placeholder="Asset" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="a in assets.filter((a) => a.id)" :key="a.id" :value="String(a.id)">
                            {{ a.name }} (${{ a.value }})
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField
                      :class="isSecurable(liabilities[index].liability_type) ? '' : 'col-span-2'"
                      :label="liabilityDescriptionLabel(liabilities[index].liability_type)"
                    >
                      <Input
                        v-model="liabilities[index].description"
                        :type="liabilities[index].liability_type === 'bnpl' ? 'number' : 'text'"
                        :min="liabilities[index].liability_type === 'bnpl' ? '0' : undefined"
                        placeholder="Optional"
                      />
                    </FormField>
                    <FormField class="justify-center" label="Will Consolidate">
                      <div class="flex h-9 items-center">
                        <Checkbox
                          :id="`consolidate_${index}`"
                          :model-value="liabilities[index].will_consolidate"
                          class="size-8 ml-8"
                          @update:model-value="(v) => (liabilities[index].will_consolidate = !!v)"
                        />
                      </div>
                    </FormField>
                  </div>
                </template>
              </SubRecordSection>

              <div class="mt-4 flex justify-end">
                <Button type="submit" :disabled="liabilityForm.processing || isReadonly">Save Liabilities</Button>
              </div>
            </form>
          </TabsContent>

          <!-- ── Tab 4: Documents ────────────────────────────────────── -->
          <TabsContent value="documents">
            <Deferred data="documents">
              <template #fallback>
                <div class="space-y-2">
                  <div class="bg-muted h-10 animate-pulse rounded" />
                  <div class="bg-muted h-10 animate-pulse rounded" />
                  <div class="bg-muted h-10 animate-pulse rounded" />
                </div>
              </template>
              <DocumentsTab
                :application-id="application.id"
                :documents="documents ?? []"
                :readonly="isReadonly"
                :upload-url="upload_url"
                :optional-doc-names="optional_doc_names"
                :bank-statement-link-options="bank_statement_link_options"
                :info-requested-at="document_info_requested_at"
                :bank-statement-reminder-sent-at="bank_statement_reminder_sent_at"
              />
            </Deferred>
          </TabsContent>

          <!-- ── Tab 5: Bank Statement ───────────────────────────────── -->
          <TabsContent value="bank_statement">
            <Deferred data="bank_statement_analyses">
              <template #fallback>
                <div class="space-y-2">
                  <div class="bg-muted h-10 animate-pulse rounded" />
                  <div class="bg-muted h-24 animate-pulse rounded" />
                </div>
              </template>
              <BankStatementTab
                :application-id="application.id"
                :analyses="(bank_statement_analyses as any) ?? []"
                :readonly="isReadonly"
              />
            </Deferred>
          </TabsContent>

          <!-- ── Tab 6: Quotation ───────────────────────────────────── -->
          <TabsContent value="quotation">
            <Deferred :data="['quotes', 'lenders']">
              <template #fallback>
                <div class="space-y-3">
                  <div class="bg-muted h-24 animate-pulse rounded-lg" />
                  <div class="bg-muted h-24 animate-pulse rounded-lg" />
                </div>
              </template>
              <QuotationTab
                :application-id="application.id"
                :amount="application.amount"
                :term-months="application.loan_term"
                :quotes="(quotes as any) ?? []"
                :lenders="(lenders as any) ?? []"
                :loan-terms="props.loan_terms"
                :readonly="isReadonly || application.status === 'approved'"
              />
            </Deferred>
          </TabsContent>
        </Tabs>
      </div>

      <!-- ── Sidebar ───────────────────────────────────────────────────── -->
      <div class="w-full lg:w-72 lg:shrink-0">
        <div class="lg:sticky lg:top-6">
          <ApplicationSidebar
            :application="application"
            :rejection-reasons="rejection_reasons"
            :optional-doc-names="optional_doc_names"
            :existing-doc-names="(documents ?? []).map((d) => d.name)"
          />
        </div>
      </div>
    </div>

    <!-- Activity & Comments — always visible at the bottom -->
    <div class="flex gap-6 px-4 pb-6 md:px-12 lg:px-24">
      <div class="min-w-0 flex-1">
        <Deferred data="comments">
          <template #fallback>
            <div class="mt-8 space-y-2">
              <div class="bg-muted h-4 w-48 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
            </div>
          </template>
          <CommentThread
            :comments="comments?.data ?? []"
            :has-more="comments?.has_more ?? false"
            :load-more-path="comments?.load_more_url ?? ''"
            :create-path="loanApplicationCommentsPath(application.id)"
            :can-comment="true"
          />
        </Deferred>
      </div>
      <div class="hidden w-72 shrink-0 lg:block" />
    </div>
    <!-- Floating chat button — scoped to this customer's conversation -->
    <ChatLauncher
      :customer-id="application.customer.id"
      :customer-name="`${application.customer.first_name} ${application.customer.last_name}`"
      :customer-phone="application.customer.phone"
      :customer-first-name="application.customer.first_name"
      :loan-application-id="application.id"
    />
  </AppLayout>
</template>
